import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useCallback } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import {
  AnnotationTagColorStyle,
  DefaultTagColorPresets,
  TagColorPaletteKey,
  TagColorPresets,
} from '../functions/tagPresets';

const kMaxSize = 30;
const kDefaultSize = 26;
const kBorderWidth = 3;
const kMinSize = kDefaultSize - kBorderWidth * 2;

const Cell = memo(
  ({
    color,
    selected,
    palette,
    style,
    onPress,
  }: {
    palette: TagColorPaletteKey;
    color: string;
    selected?: boolean;
    style?: StyleProp<ViewStyle>;
    onPress: (palette: TagColorPaletteKey) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const onCellPress = useCallback(() => {
      onPress(palette);
    }, [palette, onPress]);

    const size = rpx(selected ? kMaxSize : kDefaultSize);

    return (
      <TouchableOpacity
        onPress={onCellPress}
        css={`
          width: ${rpx(kMaxSize)}px;
          height: ${rpx(kMaxSize)}px;
          align-items: center;
          justify-content: center;
          margin-left: ${rpx(8)}px;
          margin-right: ${rpx(8)}px;
        `}
        style={style}
      >
        <View
          css={`
            width: ${rpx(size)}px;
            height: ${rpx(size)}px;
            border-radius: ${rpx(size) / 2}px;
            background-color: ${color};
            border-width: ${(size - rpx(kMinSize)) / 2}px;
            border-color: #fff;
          `}
        />
      </TouchableOpacity>
    );
  },
);

export const TagColorPicker = memo(
  ({
    colors = DefaultTagColorPresets,
    selectedColor,
    style,
    onChange,
  }: {
    selectedColor?: string;
    style?: StyleProp<ViewStyle>;
    colors?: TagColorPresets;
    onChange?: (colorStyle: AnnotationTagColorStyle) => void;
  }) => {
    const onCellPress = useCallback(
      (key: TagColorPaletteKey) => {
        const colorStyle = colors[key];
        if (colorStyle.fill !== selectedColor?.toUpperCase()) {
          onChange?.(colorStyle);
        }
      },
      [colors, onChange, selectedColor],
    );

    return (
      <View
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: center;
        `}
        style={style}
      >
        {Object.entries(colors).map(([palette, value]) => (
          <Cell
            key={palette}
            palette={palette as TagColorPaletteKey}
            color={value.fill}
            selected={selectedColor?.toUpperCase() === value.fill.toUpperCase()}
            onPress={onCellPress}
          />
        ))}
      </View>
    );
  },
);
